/**
 * Header component
 * Handles responsive behavior of the site header including:
 * - Compact header on scroll
 * - Logo switching between regular and compact states
 * - Smooth transitions
 */

import ee from '../../ee'
import { getAssetPath, testImageLoad } from '../../utils/asset-path'

class Header {
  constructor(container = document) {
    this.headerElement = container.querySelector('.header')
    this.headerLogo = container.querySelector('.header__logo')
    this.transparentLogo = container.querySelector('.header__logo-image--transparent')
    this.regularLogo = container.querySelector('.header__logo-image:not(.header__logo-image--transparent)')
    this.lastScrollY = 0
    this.isCompact = false
    this.compactScrollThreshold = 100
    this.regularScrollThreshold = 80
    this.isTransitioning = false
    this.transitionTimeout = null
    
    // Header search elements
    this.headerSearch = container.querySelector('header-search')
    this.megaMenuDisclosures = []
    this.menuDisclosureSet = new WeakSet()
    
    // SVG logo paths - use Maybourne-specific asset URLs that survive theme updates
    const maybourne = window.maybourne || {};
    
    // Get paths from config or use defaults
    const logoPath = maybourne.config?.header?.logoSvgPath || 'logo-claridges-header.svg';
    const logoSmallPath = maybourne.config?.header?.logoSmallSvgPath || 'logo-claridges-header-small.svg';
    
    // Check if we should use CSS variables instead of file paths
    if (maybourne.config?.header?.useCssVariables) {
      // Extract URLs from CSS variables
      const computedStyle = getComputedStyle(document.documentElement);
      const regularLogoVar = computedStyle.getPropertyValue(logoPath);
      const compactLogoVar = computedStyle.getPropertyValue(logoSmallPath);
      
      // Extract URLs from css url() format: url('path') -> path
      this.svgRegularLogoPath = regularLogoVar.match(/url\(['"]?([^'"]*?)['"]?\)/)?.[1] || '';
      this.svgCompactLogoPath = compactLogoVar.match(/url\(['"]?([^'"]*?)['"]?\)/)?.[1] || '';
      
      // Safari-specific fix: unescape URLs if they contain escaped characters
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      if (isSafari) {
        // Remove all backslash escaping that Safari adds
        this.svgRegularLogoPath = this.svgRegularLogoPath.replace(/\\\//g, '/').replace(/\\\./g, '.').replace(/\\\?/g, '?').replace(/\\\=/g, '=');
        this.svgCompactLogoPath = this.svgCompactLogoPath.replace(/\\\//g, '/').replace(/\\\./g, '.').replace(/\\\?/g, '?').replace(/\\\=/g, '=');
      }
    } else {
      // Use the asset path utility to get the correct paths
      this.svgRegularLogoPath = getAssetPath(logoPath);
      this.svgCompactLogoPath = getAssetPath(logoSmallPath);
    }
    this.useSvgLogos = maybourne.config?.header?.enableCompactMode !== false;
    
    // Brand-specific base heights (controls width via aspect ratio)
    this.regularBaseHeight = 80;
    this.compactBaseHeight = document.body.classList.contains('brand-berkeley') ? 48 : 40;
    // Brand sizing computed
    
    // Asset paths prepared
    
    // Don't initialize if header doesn't exist
    if (!this.headerElement) return;
    
    this.init();
  }
  
  init() {
    this.setupStyles()
    this.setupMegaMenuSearchInteraction()
    this.bindMenuDisclosureListeners()
    
    if (this.useSvgLogos && this.headerLogo) {
      this.setupSvgLogos()
    } else {
      // If not using SVG logos, just initialize other aspects
      this.bindEvents()
      this.handleScroll()
    }
  }
  
  setupStyles() {
    const styleElement = document.createElement('style')
    styleElement.textContent = `
      .header--compact {
        padding-top: 0.5rem !important;
        padding-bottom: 0.5rem !important;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        transition: all 0.3s ease;
        min-height: auto !important;
        height: auto !important;
      }
      
      .header--compact .header__logo-image {
        max-height: 40px;
        width: auto;
        transition: all 0.3s ease;
      }
      
      .header--compact .header__primary-nav-item > a,
      .header--compact .header__primary-nav-item summary {
        font-size: 0.85em;
        transition: all 0.3s ease;
      }
      
      .header--regular {
        transition: all 0.3s ease;
      }
      
      /* SVG Logo Styles */
      .header__svg-logo-container {
        display: inline-block;
        line-height: 0;
        position: relative;
        height: ${this.regularBaseHeight}px;
        transition: height 0.4s ease, width 0.4s ease;
      }
      
      .header--compact .header__svg-logo-container {
        height: ${this.compactBaseHeight}px;
      }
      
      .header__svg-logo {
        transition: opacity 0.4s ease;
        height: auto;
        backface-visibility: hidden;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }
      
      .header__svg-logo--regular {
        max-height: ${this.regularBaseHeight}px;
        position: absolute;
        z-index: 2;
      }
      
      .header__svg-logo--compact {
        max-height: ${this.compactBaseHeight}px;
        position: absolute;
        z-index: 1;
      }
      
      /* Hide Shopify default logo */
      .header__logo-image, 
      .header__logo-image--transparent,
      .header__logo-text {
        display: none !important;
      }

      /* Disable mega menu hover when search is open */
      .maybourne-search-open mega-menu-disclosure,
      .maybourne-search-open .header__primary-nav-item,
      .maybourne-search-open .header__primary-nav-item > a,
      .maybourne-search-open .header__primary-nav-item summary {
        pointer-events: none !important;
      }

      .maybourne-search-open .header__primary-nav-item:hover,
      .maybourne-search-open .header__primary-nav-item > a:hover,
      .maybourne-search-open .header__primary-nav-item summary:hover,
      .maybourne-search-open mega-menu-disclosure:hover {
        color: inherit !important;
        text-decoration: none !important;
      }

      .maybourne-search-open .header__primary-nav-item:hover:before,
      .maybourne-search-open .header__primary-nav-item > a:hover:before,
      .maybourne-search-open .header__primary-nav-item summary:hover:before {
        opacity: 0 !important;
      }

      /* Backup using :has() for supporting browsers */
      body:has(.header-search[open]) mega-menu-disclosure,
      body:has(.header-search[open]) .header__primary-nav-item {
        pointer-events: none !important;
      }
    `
    document.head.appendChild(styleElement)
  }
  
  setupSvgLogos() {
    // First verify the SVG files can be loaded
    testImageLoad(this.svgRegularLogoPath)
      .then(success => {
        if (!success) {
          console.warn('Could not load regular logo SVG, disabling SVG logos')
          this.useSvgLogos = false
          return
        }
        
        // Continue with SVG logo setup if files are available
        this.createSvgLogoElements()
      })
      .catch(error => {
        console.error('Error testing SVG availability:', error)
        this.useSvgLogos = false
      })
  }
  
  /**
   * Create SVG logo elements after verifying files exist
   */
  createSvgLogoElements() {
    // Create container for both logos with relative positioning
    const logoContainer = document.createElement('div')
    logoContainer.className = 'header__svg-logo-container'
    logoContainer.style.position = 'relative'
    logoContainer.style.display = 'inline-block'
    logoContainer.style.lineHeight = '0'
    logoContainer.style.height = this.regularBaseHeight + 'px'
    logoContainer.style.transition = 'height 0.4s ease'
    
    // Create regular logo - use absolute positioning
    const regularLogo = document.createElement('img')
    regularLogo.src = this.svgRegularLogoPath
    regularLogo.className = 'header__svg-logo header__svg-logo--regular'
    regularLogo.alt = document.title || 'Site logo'
    regularLogo.style.position = 'absolute'
    regularLogo.style.top = '50%' 
    regularLogo.style.left = '50%'
    regularLogo.style.transform = 'translate(-50%, -50%)'
    regularLogo.style.zIndex = '2'
    regularLogo.style.opacity = '1'
    regularLogo.style.maxHeight = this.regularBaseHeight + 'px'
    regularLogo.style.height = this.regularBaseHeight + 'px'
    regularLogo.style.width = 'auto'
    regularLogo.style.transition = 'opacity 0.4s ease'
    
    // Store original dimensions for the container
    let containerWidth = 240;
    
    // Force image to load and get dimensions
    regularLogo.onload = () => {
      if (regularLogo.naturalWidth && regularLogo.naturalHeight) {
        const aspectRatio = regularLogo.naturalWidth / regularLogo.naturalHeight
        const logoWidth = this.regularBaseHeight * aspectRatio;
        regularLogo.style.width = logoWidth + 'px'
        containerWidth = logoWidth;
        logoContainer.style.width = containerWidth + 'px'
      } else {
        regularLogo.style.width = '240px'
        containerWidth = 240;
        logoContainer.style.width = '240px'
      }
    }
    
    // Create compact logo - also use absolute positioning
    const compactLogo = document.createElement('img')
    compactLogo.src = this.svgCompactLogoPath
    compactLogo.className = 'header__svg-logo header__svg-logo--compact'
    compactLogo.alt = document.title || 'Site logo'
    compactLogo.style.position = 'absolute'
    compactLogo.style.top = '50%'
    compactLogo.style.left = '50%'
    compactLogo.style.transform = 'translate(-50%, -50%)'
    compactLogo.style.zIndex = '1'
    compactLogo.style.opacity = '0'
    compactLogo.style.maxHeight = this.compactBaseHeight + 'px'
    compactLogo.style.height = this.compactBaseHeight + 'px'
    compactLogo.style.width = 'auto'
    compactLogo.style.transition = 'opacity 0.4s ease'
    
    // Force image to load and get dimensions for compact logo
    compactLogo.onload = () => {
      if (compactLogo.naturalWidth && compactLogo.naturalHeight) {
        const aspectRatio = compactLogo.naturalWidth / compactLogo.naturalHeight
        compactLogo.style.width = (this.compactBaseHeight * aspectRatio) + 'px'
      } else {
        compactLogo.style.width = '120px'
      }
    }
    
    // Add both logos to container
    logoContainer.appendChild(regularLogo)
    logoContainer.appendChild(compactLogo)
    
    // Find logo link element and replace contents with our container
    const logoLink = this.headerLogo.querySelector('a')
    if (logoLink) {
      logoLink.innerHTML = ''
      logoLink.appendChild(logoContainer)
    } else {
      this.headerLogo.innerHTML = ''
      this.headerLogo.appendChild(logoContainer)
    }
    
    // Store references for later use
    this.svgLogoContainer = logoContainer
    this.svgRegularLogo = regularLogo
    this.svgCompactLogo = compactLogo
    
    // Bind scroll events and check position
    this.bindEvents()
    this.handleScroll()
  }
  
  /**
   * Setup interaction between mega menu hover and predictive search
   * Disables mega menu hover when search is open
   */
  setupMegaMenuSearchInteraction() {
    if (!this.headerSearch) return
    
    // Wait for theme components to be initialized
    setTimeout(() => {
      this.patchMegaMenuHover()
      this.bindMenuDisclosureListeners()
    }, 100)
    
    // Additional delayed patch in case theme loads later
    setTimeout(() => {
      this.patchMegaMenuHover()
      this.bindMenuDisclosureListeners()
    }, 500)
    
    // Listen for search open/close events
    this.headerSearch.addEventListener('dialog:before-show', () => {
      this.disableMegaMenuHover()
      this.addSearchOpenClass()
      this.setHeaderHeightVariable() // Set the height variable when search opens
    })
    
    this.headerSearch.addEventListener('dialog:after-hide', () => {
      this.enableMegaMenuHover()
      this.removeSearchOpenClass()
    })
  }
  
  /**
   * Measures the current header height and sets a CSS variable.
   */
  setHeaderHeightVariable() {
    const headerSection = document.querySelector('.shopify-section--header');
    if (headerSection) {
      const height = headerSection.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--header-actual-height', `${height}px`);
    }
  }

  /**
   * Patch the theme's mega menu hover functionality
   */
  patchMegaMenuHover() {
    // Find all mega menu disclosure elements
    const megaMenus = document.querySelectorAll('mega-menu-disclosure')
    
    // Patch the prototype method globally
    if (window.customElements && window.customElements.get('mega-menu-disclosure')) {
      const MegaMenuClass = window.customElements.get('mega-menu-disclosure')
      if (MegaMenuClass && !MegaMenuClass.prototype._maybournePatched) {
        const originalDetectHover = MegaMenuClass.prototype.detectHover
        const self = this
        
        MegaMenuClass.prototype.detectHover = function(event) {
          // Check if header search is open
          if (self.headerSearch && self.headerSearch.open) {
            return // Block hover behavior when search is open
          }
          
          // Call original method if search is not open
          if (originalDetectHover) {
            return originalDetectHover.call(this, event)
          }
        }
        
        MegaMenuClass.prototype._maybournePatched = true
        MegaMenuClass.prototype._maybourneOriginalDetectHover = originalDetectHover
      }
    }
    
    // Track individual instances
    megaMenus.forEach((menu) => {
      this.megaMenuDisclosures.push(menu)
      // Also bind to details toggle events to track open state
      const details = menu.querySelector('details.header__menu-disclosure')
      if (details && !this.menuDisclosureSet.has(details)) {
        details.addEventListener('toggle', () => this.updateMegaOpenClass())
        this.menuDisclosureSet.add(details)
      }
    })
  }

  /**
   * Ensure we listen to all header menu disclosure elements (mega and dropdown)
   * so we can toggle a body class while any menu is open
   */
  bindMenuDisclosureListeners() {
    const allDetails = document.querySelectorAll('details.header__menu-disclosure')
    allDetails.forEach((details) => {
      if (!this.menuDisclosureSet.has(details)) {
        details.addEventListener('toggle', () => this.updateMegaOpenClass())
        this.menuDisclosureSet.add(details)
      }
    })
    // Evaluate initial state
    this.updateMegaOpenClass()
  }

  /**
   * Toggle a class while any header menu disclosure is open
   */
  updateMegaOpenClass() {
    const anyOpen = document.querySelector('details.header__menu-disclosure[open]') !== null
    document.body.classList.toggle('maybourne-mega-open', anyOpen)
    if (this.headerElement) {
      this.headerElement.classList.toggle('maybourne-mega-open', anyOpen)
    }
  }
  
  /**
   * Disable mega menu hover behavior
   */
  disableMegaMenuHover() {
    this.megaMenuDisclosures.forEach((menu) => {
      // Check the correct open property
      const isOpen = menu.disclosureElement?.open || menu.open === true
      
      if (isOpen) {
        if (typeof menu.toggle === 'function') {
          menu.toggle(false)
        } else if (menu.close && typeof menu.close === 'function') {
          menu.close()
        }
      }
    })
  }
  
  /**
   * Enable mega menu hover behavior
   */
  enableMegaMenuHover() {
    // Hover behavior is re-enabled automatically when search closes
    // since our patch checks the search state
  }

  /**
   * Add CSS class to disable mega menu hover via CSS
   */
  addSearchOpenClass() {
    document.body.classList.add('maybourne-search-open')
    if (this.headerElement) {
      this.headerElement.classList.add('maybourne-search-open')
    }
  }

  /**
   * Remove CSS class to re-enable mega menu hover via CSS
   */
  removeSearchOpenClass() {
    document.body.classList.remove('maybourne-search-open')
    if (this.headerElement) {
      this.headerElement.classList.remove('maybourne-search-open')
    }
  }
  
  bindEvents() {
    this.boundHandleScroll = this.throttledScrollHandler.bind(this)
    this.boundCheckBreakpoints = this.checkBreakpoints.bind(this)
    
    window.addEventListener('scroll', this.boundHandleScroll)
    window.addEventListener('resize', this.boundCheckBreakpoints)
  }
  
  throttledScrollHandler() {
    if (!this.ticking) {
      window.requestAnimationFrame(() => {
        this.handleScroll()
        this.ticking = false
      })
      this.ticking = true
    }
  }
  
  handleScroll() {
    if (this.isTransitioning) {
      return
    }
    
    const currentScrollY = window.scrollY
    
    if (currentScrollY > this.compactScrollThreshold && !this.isCompact) {
      this.makeCompact()
    } else if (currentScrollY <= this.regularScrollThreshold && this.isCompact) {
      this.makeRegular()
    }
    
    this.lastScrollY = currentScrollY
  }
  
  makeCompact() {
    this.isTransitioning = true
    
    this.isCompact = true
    this.headerElement.classList.add('header--compact')
    this.headerElement.classList.remove('header--regular')
    
    if (this.useSvgLogos) {
      if (this.svgRegularLogo && this.svgCompactLogo) {
        this.svgRegularLogo.style.opacity = '0'
        this.svgCompactLogo.style.opacity = '1'
        this.svgCompactLogo.style.zIndex = '2'
        this.svgRegularLogo.style.zIndex = '1'
        
        // Move the compact logo down slightly
        this.svgCompactLogo.style.transform = 'translate(-50%, -35%)'
        
        const container = this.svgRegularLogo.parentNode;
        if (container && container.classList.contains('header__svg-logo-container')) {
          container.style.height = this.compactBaseHeight + 'px';
        }
      }
    } 
    else if (this.transparentLogo && this.regularLogo) {
      this.transparentLogo.style.display = 'block'
      this.regularLogo.style.display = 'none'
    }
    
    document.dispatchEvent(new CustomEvent('header:compact', {
      bubbles: true,
      detail: { isCompact: true }
    }))
    
    clearTimeout(this.transitionTimeout)
    this.transitionTimeout = setTimeout(() => {
      this.isTransitioning = false
    }, 400)
  }
  
  makeRegular() {
    this.isTransitioning = true
    
    this.isCompact = false
    this.headerElement.classList.remove('header--compact')
    this.headerElement.classList.add('header--regular')
    
    if (this.useSvgLogos) {
      if (this.svgRegularLogo && this.svgCompactLogo) {
        this.svgRegularLogo.style.opacity = '1'
        this.svgCompactLogo.style.opacity = '0'
        this.svgRegularLogo.style.zIndex = '2'
        this.svgCompactLogo.style.zIndex = '1'
        
        // Reset the compact logo transform
        this.svgCompactLogo.style.transform = 'translate(-50%, -50%)'
        
        const container = this.svgRegularLogo.parentNode;
        if (container && container.classList.contains('header__svg-logo-container')) {
          container.style.height = this.regularBaseHeight + 'px';
        }
      }
    }
    else if (this.transparentLogo && this.regularLogo) {
      this.transparentLogo.style.display = 'none'
      this.regularLogo.style.display = 'block'
    }
    
    document.dispatchEvent(new CustomEvent('header:compact', {
      bubbles: true,
      detail: { isCompact: false }
    }))
    
    clearTimeout(this.transitionTimeout)
    this.transitionTimeout = setTimeout(() => {
      this.isTransitioning = false
    }, 400)
  }
  
  checkBreakpoints() {
    this.handleScroll()
  }
  
  destroy() {
    // Cleanup scroll event
    window.removeEventListener('scroll', this.throttledScrollHandler)
    
    // Cleanup cart count dot
    if (this.cartCountDot) {
      this.cartCountDot.destroy();
    }
    
    // Remove mutation observer
    if (this.observer) {
      this.observer.disconnect()
    }
    
    // Restore original mega menu hover methods
    if (window.customElements && window.customElements.get('mega-menu-disclosure')) {
      const MegaMenuClass = window.customElements.get('mega-menu-disclosure')
      if (MegaMenuClass && MegaMenuClass.prototype._maybournePatched) {
        if (MegaMenuClass.prototype._maybourneOriginalDetectHover) {
          MegaMenuClass.prototype.detectHover = MegaMenuClass.prototype._maybourneOriginalDetectHover
        }
        delete MegaMenuClass.prototype._maybournePatched
        delete MegaMenuClass.prototype._maybourneOriginalDetectHover
      }
    }
    
    clearTimeout(this.transitionTimeout)
    
    if (this.headerElement) {
      this.headerElement.classList.remove('header--compact', 'header--regular', 'maybourne-search-open')
    }
    
    // Clean up body class
    document.body.classList.remove('maybourne-search-open')
    
    this.headerElement = null
    this.headerLogo = null
    this.headerSearch = null
    this.megaMenuDisclosures = []
    this.svgRegularLogo = null
    this.svgCompactLogo = null
  }

  /**
   * Initialize cart count dot enhancement
   * Transforms the existing cart-dot into a cart count bubble
   */
  initCartCountDot() {
    // Find the cart dot element
    this.cartDot = document.querySelector('.header__cart-dot');
    
    if (!this.cartDot) {
      console.warn('Maybourne Header: cart dot element not found');
      return;
    }

    // Set up abort controller for event listeners
    this.cartDotAbortController = new AbortController();

    // Initialize with current cart count
    this.updateCartCount();

    // Listen for cart changes
    document.addEventListener('cart:change', (event) => {
      this.updateCartCount(event.detail.cart.item_count);
    }, { signal: this.cartDotAbortController.signal });

    // Listen for cart count updates (Buy It Now without opening drawer)
    document.addEventListener('cart:count-update', (event) => {
      this.updateCartCount(event.detail.cart.item_count);
    }, { signal: this.cartDotAbortController.signal });

    // Listen for cart refresh
    document.addEventListener('cart:refresh', () => {
      this.updateCartCount();
    }, { signal: this.cartDotAbortController.signal });

    // Listen for page show (back/forward navigation)
    window.addEventListener('pageshow', () => {
      this.updateCartCount();
    }, { signal: this.cartDotAbortController.signal });

    // Cart count dot enhanced
  }

  async updateCartCount(count = null) {
    if (!this.cartDot) return;

    // If count not provided, fetch from server
    if (count === null) {
      try {
        const response = await fetch(`${Shopify.routes.root}cart.js`);
        const cart = await response.json();
        count = cart.item_count;
      } catch (error) {
        console.error('Maybourne Header: Failed to fetch cart', error);
        return;
      }
    }

    // Update visibility and content
    if (count > 0) {
      this.cartDot.textContent = count > 99 ? '99+' : count.toString();
      this.cartDot.classList.add('is-visible');
      this.cartDot.setAttribute('aria-label', `${count} item${count !== 1 ? 's' : ''} in cart`);
    } else {
      this.cartDot.textContent = '';
      this.cartDot.classList.remove('is-visible');
      this.cartDot.removeAttribute('aria-label');
    }
  }
}

// Initialize header on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  const header = new Header();
  
  // Initialize cart count dot enhancement
  header.initCartCountDot();
  
  // Store reference globally for potential external access
  window.maybourneHeader = header;
})

export default Header 